import { getApiBaseUrl } from "../configs/apiEnv";

const PLACEHOLDER = "/assets/images/hethong.jpg";

/**
 * Logic chuẩn hóa SIÊU MẠNH cho User Frontend.
 */
export const normalizeImageUrl = (url) => {
  if (!url || url === "null" || url === "undefined") return PLACEHOLDER;
  if (typeof url !== "string") return url;
  if (url.startsWith('blob:') || url.startsWith('data:') || /^https?:\/\//i.test(url)) return url;

  const host = getApiBaseUrl();
  const baseUrl = host.replace(/\/$/, "");

  let cleanPath = url.replace(/\\/g, "/");

  const markers = ["images/", "banners/", "posts/", "settings/", "branches/", "events/", "service-groups/", "service-items/", "typical-customers/", "jobs/", "about-us/", "about/"];

  let corePath = "";
  for (const marker of markers) {
    const index = cleanPath.indexOf(marker);
    if (index !== -1) {
      corePath = cleanPath.substring(index);
      if (marker !== "images/" && !corePath.startsWith("images/")) {
        corePath = "images/" + corePath;
      }
      break;
    }
  }

  if (!corePath) {
    corePath = "images/" + cleanPath.replace(/^[./]+/, "").replace(/^public\//, "");
  }

  corePath = corePath.replace(/^images\/images\//, "images/");

  const finalUrl = `${baseUrl}/${corePath}`;
  return finalUrl;
};

export const fixHtmlImagePaths = (html) => {
  if (!html) return "";

  return html.replace(/src=(["'])([^"']+)\1/g, (match, quote, path) => {
    if (path.startsWith("http") || path.startsWith("data:") || path.startsWith("blob:") || path.includes("assets/")) {
      return match;
    }
    const normalized = normalizeImageUrl(path);
    return `src=${quote}${normalized}${quote}`;
  });
};

/**
 * processEmbedUrls
 *
 * Tự động chuyển đổi URL TikTok / YouTube / Google Maps dạng text thuần thành iframe embed.
 * Admin chỉ cần paste URL vào Quill trên 1 dòng riêng — không cần làm gì thêm.
 *
 * TikTok dùng iframe player/v1 — load ngay lập tức, KHÔNG cần SDK bên ngoài.
 * Google Maps hỗ trợ cả link rút gọn (maps.app.goo.gl) và link đầy đủ (google.com/maps).
 */
export const processEmbedUrls = (html) => {
  if (!html) return "";

  // Helper: tạo TikTok iframe từ videoId
  const makeTikTokIframe = (videoId) => {
    return `<div style="display:flex;justify-content:center;margin:1.5rem 0;width:100%;">
  <iframe
    src="https://www.tiktok.com/player/v1/${videoId}?autoplay=0&loop=0&rel=0"
    style="width:360px;height:640px;max-width:100%;border:none;border-radius:16px;box-shadow:0 8px 32px rgba(0,0,0,0.15);"
    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
    allowfullscreen
    title="TikTok video player"
  ></iframe>
</div>`;
  };

  // Helper: trích xuất TikTok video ID từ bất kỳ chuỗi nào
  const extractTikTokId = (text) => {
    // Ưu tiên path /video/ID hoặc /embed/v2/ID hoặc /player/v1/ID
    const pathMatch = text.match(/\/(?:video|embed\/v\d+|player\/v\d+)\/(\d{15,21})/i);
    if (pathMatch) return pathMatch[1];
    // Fallback: 15-21 chữ số liên tiếp
    const numMatch = text.match(/(\d{15,21})/);
    return numMatch ? numMatch[1] : null;
  };

  /**
   * Helper: Tạo Google Maps iframe embed từ URL bất kỳ.
   * - maps.app.goo.gl → BỎ QUA (không embed được, sẽ hiện bản đồ thế giới trống)
   * - google.com/maps/embed?pb=... → dùng trực tiếp
   * - google.com/maps/dir/... (chỉ đường) → extract tọa độ điểm ĐÍCH từ data=
   * - google.com/maps có @lat,lng → embed bằng tọa độ trung tâm
   */
  const makeGoogleMapsEmbed = (rawUrl) => {
    // Link rút gọn goo.gl → KHÔNG nhúng được, bỏ qua (trả về null)
    if (/maps\.app\.goo\.gl/i.test(rawUrl)) {
      return null;
    }

    const iframeWrapper = (src) =>
      `<div style="width:100%;margin:1.5rem 0;border-radius:12px;overflow:hidden;box-shadow:0 4px 20px rgba(0,0,0,0.12);">` +
      `<iframe src="${src}" width="100%" height="420" style="border:none;display:block;" allowfullscreen loading="lazy" referrerpolicy="no-referrer-when-downgrade" title="Google Maps"></iframe>` +
      `</div>`;

    // Link embed đầy đủ google.com/maps/embed?pb=... → dùng trực tiếp
    if (/google\.com\/maps\/embed/i.test(rawUrl)) {
      return iframeWrapper(rawUrl);
    }

    // URL dạng /maps/dir/ (chỉ đường A→B):
    // @lat,lng trong URL này là tọa độ VIEWPORT, không phải tọa độ garage.
    // Phải extract tọa độ điểm ĐÍCH thực sự từ phần data= cuối URL.
    // Ví dụ: data=...!1s0x...!2m2!1d105.8041922!2d21.0324366 → đây là tọa độ đích.
    if (/google\.com\/maps\/dir\//i.test(rawUrl)) {
      // Tìm tất cả cặp tọa độ dạng 1d{lng}!2d{lat} trong data=
      const waypointMatches = [...rawUrl.matchAll(/1d(-?\d+\.\d+)!2d(-?\d+\.\d+)/g)];
      if (waypointMatches.length > 0) {
        // Lấy waypoint CUỐI CÙNG = điểm đích
        const last = waypointMatches[waypointMatches.length - 1];
        const destLng = last[1];
        const destLat = last[2];
        // Lấy zoom từ @lat,lng,{zoom}z nếu có
        const zoomMatch = rawUrl.match(/@-?\d+\.\d+,-?\d+\.\d+,(\d+(?:\.\d+)?)z/);
        const zoom = Math.round(parseFloat(zoomMatch ? zoomMatch[1] : "17"));
        return iframeWrapper(`https://maps.google.com/maps?q=${destLat},${destLng}&z=${zoom}&output=embed`);
      }
      // Nếu không có data= waypoint, thử extract place name từ URL path làm query
      const pathParts = rawUrl.split('/maps/dir/');
      if (pathParts[1]) {
        const segments = pathParts[1].split('/');
        // Segment cuối cùng trước @ hoặc /data là địa điểm đích
        const destSegment = segments.find(s => s && !s.startsWith('@') && !s.startsWith('data'));
        if (destSegment) {
          const placeName = decodeURIComponent(destSegment.replace(/\+/g, ' '));
          return iframeWrapper(`https://maps.google.com/maps?q=${encodeURIComponent(placeName)}&output=embed`);
        }
      }
    }

    // google.com/maps với tọa độ @lat,lng (dạng place thông thường)
    const coordMatch = rawUrl.match(/@(-?\d+\.\d+),(-?\d+\.\d+)(?:,(\d+(?:\.\d+)?)z)?/);
    if (coordMatch) {
      const lat = coordMatch[1];
      const lng = coordMatch[2];
      const zoom = Math.round(parseFloat(coordMatch[3] || "15"));
      return iframeWrapper(`https://maps.google.com/maps?q=${lat},${lng}&z=${zoom}&output=embed`);
    }

    // Không nhận ra dạng URL → bỏ qua
    return null;
  };



  // Helper: kiểm tra URL có phải Google Maps không
  const isGoogleMapsUrl = (url) =>
    /(?:maps\.app\.goo\.gl|(?:www\.)?google\.com\/maps)/i.test(url);

  // Helper: trích URL Google Maps từ đoạn HTML bất kỳ
  const extractGoogleMapsUrl = (html) => {
    const m = html.match(/https?:\/\/(?:maps\.app\.goo\.gl|(?:www\.)?google\.com\/maps)[^\s"'<]*/i);
    return m ? m[0] : null;
  };

  // ── GOOGLE MAPS — xử lý TRƯỚC TikTok để tránh conflict khi quét <p> ──

  // GM-1: <p> chứa link Google Maps (text thuần hoặc <a href>)
  html = html.replace(/<p[^>]*>[\s\S]*?<\/p>/gi, (pTag) => {
    if (!isGoogleMapsUrl(pTag)) return pTag;
    const url = extractGoogleMapsUrl(pTag);
    if (!url) return pTag;
    const embed = makeGoogleMapsEmbed(url);
    return embed ?? pTag; // null → giữ nguyên (goo.gl không embed được)
  });

  // GM-2: <a href="MAPS_URL"> đứng ngoài <p>
  html = html.replace(
    /<a[^>]*href=["'](https?:\/\/(?:maps\.app\.goo\.gl|(?:www\.)?google\.com\/maps)[^"']*)["'][^>]*>[\s\S]*?<\/a>/gi,
    (match, href) => makeGoogleMapsEmbed(href) ?? match // null → giữ nguyên thẻ <a>
  );

  // GM-3: URL text thuần không trong thẻ nào
  html = html.replace(
    /(^|[\n>])\s*(https?:\/\/(?:maps\.app\.goo\.gl|(?:www\.)?google\.com\/maps)[^\s<"']*)\s*($|[\n<])/gim,
    (match, before, url, after) => {
      const embed = makeGoogleMapsEmbed(url);
      return embed ? `${before}${embed}${after}` : match; // null → giữ nguyên
    }
  );

  // ── 1. YouTube iframe ĐÃ CÓ SẴN (do Quill Video toolbar tạo) ──
  html = html.replace(
    /(<iframe[^>]+src=["'][^"']*(?:youtube\.com\/embed|youtu\.be)[^"']*["'][^>]*>[\s\S]*?<\/iframe>)/gi,
    (match) => {
      return `<div style="position:relative;width:100%;padding-bottom:56.25%;height:0;overflow:hidden;border-radius:12px;margin:1.5rem 0;box-shadow:0 4px 20px rgba(0,0,0,0.10);">
  ${match.replace(/style="[^"]*"/gi, '').replace(/<iframe/i, '<iframe style="position:absolute;top:0;left:0;width:100%;height:100%;border:none;border-radius:12px;"')}
</div>`;
    }
  );

  // ── 2. TikTok iframe ĐÃ CÓ SẴN → chuẩn hoá sang player/v1 ──
  html = html.replace(
    /<iframe[^>]+src=["'](https?:\/\/(?:[a-zA-Z0-9-]+\.)?tiktok\.com\/[^"']+)["'][^>]*>[\s\S]*?<\/iframe>/gi,
    (match, src) => {
      const videoId = extractTikTokId(src);
      if (!videoId) return match;
      return makeTikTokIframe(videoId);
    }
  );

  // ── 3. blockquote.tiktok-embed ĐÃ CÓ SẴN → đổi sang player/v1 ──
  html = html.replace(
    /<blockquote[^>]*tiktok-embed[^>]*>[\s\S]*?<\/blockquote>/gi,
    (match) => {
      const videoId = extractTikTokId(match);
      if (!videoId) return match;
      return makeTikTokIframe(videoId);
    }
  );

  // ── 4. Quét TỪNG THẺ <p> — nếu có tiktok.com thì chuyển thành iframe ──
  html = html.replace(/<p[^>]*>[\s\S]*?<\/p>/gi, (pTag) => {
    if (!pTag.includes('tiktok.com')) return pTag;
    const videoId = extractTikTokId(pTag);
    if (!videoId) return pTag;
    return makeTikTokIframe(videoId);
  });

  // ── 5. TikTok link trong thẻ <a> còn sót lại (nằm ngoài <p>) ──
  html = html.replace(
    /<a[^>]*href=["']([^"']*tiktok\.com[^"']*)["'][^>]*>[\s\S]*?<\/a>/gi,
    (match, href) => {
      const videoId = extractTikTokId(href);
      if (!videoId) return match;
      return makeTikTokIframe(videoId);
    }
  );

  // ── 6. YouTube URL trong thẻ <p> ──
  html = html.replace(
    /(<p[^>]*>)\s*(https?:\/\/(?:www\.youtube\.com\/watch\?v=|youtu\.be\/|www\.youtube\.com\/embed\/)([a-zA-Z0-9_-]{11})[^\s<]*)\s*(<\/p>)/gi,
    (match, openTag, url, videoId, closeTag) => {
      return `
<div style="position:relative;width:100%;padding-bottom:56.25%;height:0;overflow:hidden;border-radius:12px;margin:1.5rem 0;box-shadow:0 4px 20px rgba(0,0,0,0.10);">
  <iframe
    src="https://www.youtube.com/embed/${videoId}"
    style="position:absolute;top:0;left:0;width:100%;height:100%;border:none;border-radius:12px;"
    allowfullscreen
    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
    title="YouTube video"
  ></iframe>
</div>`;
    }
  );

  // ── 7. YouTube URL dạng text thuần ──
  html = html.replace(
    /(^|[\n>])\s*(https?:\/\/(?:www\.youtube\.com\/watch\?v=|youtu\.be\/|www\.youtube\.com\/embed\/)([a-zA-Z0-9_-]{11})[^\s<]*)\s*($|[\n<])/gim,
    (match, before, url, videoId, after) => {
      return `${before}
<div style="position:relative;width:100%;padding-bottom:56.25%;height:0;overflow:hidden;border-radius:12px;margin:1.5rem 0;box-shadow:0 4px 20px rgba(0,0,0,0.10);">
  <iframe
    src="https://www.youtube.com/embed/${videoId}"
    style="position:absolute;top:0;left:0;width:100%;height:100%;border:none;border-radius:12px;"
    allowfullscreen
    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
    title="YouTube video"
  ></iframe>
</div>${after}`;
    }
  );

  // ── 8. YouTube link trong thẻ <a> đứng một mình trong <p> ──
  html = html.replace(
    /(<p[^>]*>)\s*<a[^>]+href="(https?:\/\/(?:www\.youtube\.com\/watch\?v=|youtu\.be\/|www\.youtube\.com\/embed\/)([a-zA-Z0-9_-]{11})[^"]*)"[^>]*>[^<]*<\/a>\s*(<\/p>)/gi,
    (match, openTag, url, videoId, closeTag) => {
      return `
<div style="position:relative;width:100%;padding-bottom:56.25%;height:0;overflow:hidden;border-radius:12px;margin:1.5rem 0;box-shadow:0 4px 20px rgba(0,0,0,0.10);">
  <iframe
    src="https://www.youtube.com/embed/${videoId}"
    style="position:absolute;top:0;left:0;width:100%;height:100%;border:none;border-radius:12px;"
    allowfullscreen
    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
    title="YouTube video"
  ></iframe>
</div>`;
    }
  );


  return html;
};
